/**
 * Creates the view object for working with Profile form data.
 */

import { formatFullName } from '../formats'

const getPrivate = (canSetPublic, visibility) => (!canSetPublic ? true : visibility !== 'PUBLIC')

const getVisibility = (canSetPublic, privateValue) => (canSetPublic && !privateValue ? 'PUBLIC' : 'PRIVATE')

const getProfileItemView = ({ profile, name, label, ...rest }) => ({
  name,
  label,
  value: profile[name]?.value ?? '',
  asProfileItem: (formValues) => ({ [name]: { value: formValues[name] } }),
  ...rest,
})

export default function getProfileView({ profile, canSetPublic, t }) {
  return {
    name: getProfileItemView({ profile, name: 'name', label: t('name.label', 'Name') }),
    contactId: getProfileItemView({ profile, name: 'contactId', label: t('contactid-profile.label', 'Contact ID') }),
    fullName: {
      name: 'fullName',
      label: t('full-name.label', 'Full Name'),
      value: formatFullName({
        givenNames: profile.fullName?.givenName?.value,
        surnames: profile.fullName?.surname?.value,
      }),
      privateName: 'fullNamePrivate',
      canSetPublic,
      // Show as private only if BOTH fields are private, so users are aware of any public exposure
      private: getPrivate(
        canSetPublic,
        profile.fullName?.givenName?.visibility === 'PRIVATE' && profile.fullName?.surname?.visibility === 'PRIVATE'
          ? 'PRIVATE'
          : 'PUBLIC'
      ),
      givenName: {
        name: 'firstNames',
        label: t('first-names.label', 'First Names'),
        value: profile.fullName?.givenName?.value ?? '',
        visibility: profile.fullName?.givenName?.visibility,
      },
      surname: {
        name: 'lastNames',
        label: t('last-names.label', 'Last Names'),
        value: profile.fullName?.surname?.value ?? '',
        visibility: profile.fullName?.surname?.visibility,
      },
      asProfileItem: (formValues) => ({
        fullName: {
          givenName: {
            value: formValues.firstNames,
            visibility: getVisibility(canSetPublic, formValues.fullNamePrivate),
          },
          surname: {
            value: formValues.lastNames,
            visibility: getVisibility(canSetPublic, formValues.fullNamePrivate),
          },
        },
      }),
    },
    country: {
      name: 'country',
      label: t('profile.country-location.label', 'Country or Location'),
      value: String(profile.location?.country?.stdRepId ?? ''),
      privateName: 'countryPrivate',
      canSetPublic,
      private: getPrivate(canSetPublic, profile.location?.country?.visibility),
      asProfileItem: ({ country: formValue, countryPrivate }) => {
        const country = formValue ? +formValue : null
        const countryIsChanging = profile.location?.country?.stdRepId !== country
        const stateOrProvince = {
          stdRepId: country === null || countryIsChanging ? null : profile.location?.stateOrProvince?.stdRepId,
          visibility:
            countryIsChanging || getPrivate(canSetPublic, countryPrivate)
              ? 'PRIVATE'
              : getVisibility(canSetPublic, profile.location?.stateOrProvince?.visibility),
        }
        return {
          location: {
            country: {
              stdRepId: country,
              visibility: getVisibility(canSetPublic, countryPrivate),
            },
            stateOrProvince,
          },
        }
      },
    },
    stateOrProvince: {
      name: 'stateOrProvince',
      label: t('profile.state-province.label', 'State or Province'),
      value: String(profile.location?.stateOrProvince?.stdRepId ?? ''),
      privateName: 'stateOrProvincePrivate',
      canSetPublic,
      private: getPrivate(canSetPublic, profile.location?.stateOrProvince?.visibility),
      asProfileItem: ({ stateOrProvince: formValue, stateOrProvincePrivate }) => {
        const value = formValue ? +formValue : null
        const country = {
          stdRepId: profile.location?.country?.stdRepId,
          visibility: profile.location?.country?.visibility,
        }
        const stateOrProvince = {
          stdRepId: value,
          visibility: getPrivate(canSetPublic, profile.location?.country?.visibility)
            ? 'PRIVATE'
            : getVisibility(canSetPublic, stateOrProvincePrivate),
        }
        return {
          location: {
            country,
            stateOrProvince,
          },
        }
      },
    },
    phone: getContactCardMethodView({
      profile,
      name: 'phone',
      label: t('profile.phone-label', 'Phone'),
      method: 'PHONE',
      type: 'UNKNOWN',
      canSetPublic,
    }),
    email: getContactCardMethodView({
      profile,
      name: 'email',
      label: t('common-ui:email.address.abbr', 'Email'),
      method: 'EMAIL',
      type: 'UNKNOWN',
      canSetPublic,
    }),
    facebook: getContactCardMethodView({
      profile,
      name: 'facebook',
      label: t('profile.facebook-username.label', 'Facebook'),
      method: 'SOCIAL',
      type: 'FACEBOOK',
      canSetPublic,
    }),
    whatsApp: getContactCardMethodView({
      profile,
      name: 'whatsApp',
      label: t('profile.whatsapp-number.label', 'WhatsApp'),
      method: 'SOCIAL',
      type: 'WHATSAPP',
      canSetPublic,
    }),
  }
}

function getContactCardMethodView({ profile, name, label, method, type, canSetPublic }) {
  const methods = profile.contactInfo?.methods || []
  const index = methods.findIndex(
    (x) => x.method === method && x.type === type && x.preferredUsages?.includes('CONTACT_CARD')
  )
  const privateName = `${name}Private`
  return {
    name,
    label,
    value: methods[index]?.value ?? '',
    privateName,
    canSetPublic,
    private: getPrivate(canSetPublic, methods[index]?.visibility),
    asProfileItem: (formValues) => {
      const newMethods = methods.filter((x, i) => i !== index)
      const value = formValues[name]
      if (value) {
        newMethods.push({
          method,
          type,
          value,
          visibility: getVisibility(canSetPublic, formValues[privateName]),
          preferredUsages: ['CONTACT_CARD'],
        })
      }
      return { contactInfo: { methods: newMethods } }
    },
  }
}
